import React, { useState, useEffect } from "react";
import styled from "styled-components";
import ReactFlagsSelect, { La } from "react-flags-select";
import { useLocation } from "react-router-dom";
import Header from "../Header";
import { useHistory } from "react-router-dom";
import { colors } from "../../styles";
import arbitrum from "../../assets/images/arbitrum.png";
import twitter from "../../assets/images/twitter.png";
import telegram from "../../assets/images/telegram.png";
import discord from "../../assets/images/discord.png";
import wallet from "../../assets/images/wallet.png";
import { useStateContext } from "../../providers/StateProvider";
import { LANGUAGE_TYPES } from "../../constants/context";
import { useTranslation } from "react-i18next";
import { PATHS } from "../../constants/types";
import { number } from "prop-types";

interface MenuAreaProps {
  displayMenu: boolean;
  connected: boolean;
  address: string;
  chainId: number;
  killSession: () => void;
  openModal: () => void;
}

interface MenuContainerProps {
  status: boolean;
}
interface MenuItemProps {
  isActive: boolean;
}

const MenuContainer = styled.ul<MenuContainerProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  transition: all 0.3s ease-in-out;
  width: 50%;
  justify-content: space-between;
  @media (max-width: 960px) {
    flex-direction: column;
    transform: translateY(${(props) => (props.status ? "-100%" : "40%")});
    background: rgb(${colors.main});
    opacity: 0.9;
    width: 100%;
    position: absolute;
    z-index: 10;
    border-radius: 20px;
    border: 1px solid rgba(${colors.border});
  }
`;
const MmenuItem = styled.li<MenuItemProps>`
  color: rgb(${colors.fontColor});
  font-size: 20px;
  font-family: "Montserrat-Medium";
  margin: 0 0.5em;
  padding-bottom: 3px;
  border-bottom: ${(props) =>
    props.isActive ? "1px solid white" : "1px solid transparent"};
  @media (max-width: 1100px) {
    margin: 0 0.3em;
  }
  @media (max-width: 960px) {
    margin: 0.5em 0;
  }
  &:hover {
    cursor: pointer;
    // text-shadow: 1px 1px 2px white, 0 0 1em white, 0 0 0.2em darkblue;
    border-bottom: 1px solid white;
  }
`;
const SocialIcon = styled.img`
  width: 25px;
  height: 25px;
  margin: 0.5em;
  &:hover {
    cursor: pointer;
  }
`;
const ArbitrumIcon = styled.img`
  width: 22px;
  height: 25px;
  margin: 0.5em;
  &:hover {
    cursor: pointer;
  }
`;

const TextMenuIcon = styled.div`
  display: flex;
  font-family: "Montserrat-SemiBold";
  flex-direction: column;
  margin: 0 1em;
  background-size: contain;
  color: red;
  font-size: 13px;
  text-align: center;
`;

const MenuArea: React.FC<MenuAreaProps> = ({
  killSession,
  openModal,
  displayMenu,
  connected,
  address,
  chainId,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [select, setSelect] = useState("GB");
  const [navIndex, setNavIndex] = useState(0);
  const { language, setLanguage } = useStateContext();

  useEffect(() => {
    const { lang, nav } = analyseUrl();
    if (lang === LANGUAGE_TYPES.CHINESE) {
      setSelect("CN");
      setLanguage(LANGUAGE_TYPES.CHINESE);
    } else {
      setSelect("GB");
      setLanguage(LANGUAGE_TYPES.ENGLISH);
    }
    setNavIndex(nav);
  }, [pathname]);

  let history = useHistory();
  const navigateTo = (lang: string, path: string) => {
    if (lang === LANGUAGE_TYPES.CHINESE) history.push(`/cn/${path}`);
    else history.push(`/${path}`);
  };

  const analyseUrl = () => {
    const segments = pathname.split("/").filter((segment) => segment !== "");
    if (segments.length === 2) {
      if (segments[0] === "cn") {
        if (segments[1] === "sale")
          return { lang: LANGUAGE_TYPES.CHINESE, nav: 1 };
        else if (segments[1] === "bermuda")
          return { lang: LANGUAGE_TYPES.CHINESE, nav: 2 };
        else if (segments[1] === "otc")
          return { lang: LANGUAGE_TYPES.CHINESE, nav: 3 };
        else if (segments[1] === "tortuga")
          return { lang: LANGUAGE_TYPES.CHINESE, nav: 4 };
      }
      return { lang: LANGUAGE_TYPES.CHINESE, nav: 0 };
    } else if (segments.length === 1) {
      if (segments[0] === "cn") return { lang: LANGUAGE_TYPES.CHINESE, nav: 0 };
      else if (segments[0] === "sale")
        return { lang: LANGUAGE_TYPES.ENGLISH, nav: 1 };
      else if (segments[0] === "bermuda")
        return { lang: LANGUAGE_TYPES.ENGLISH, nav: 2 };
      else if (segments[0] === "otc")
        return { lang: LANGUAGE_TYPES.ENGLISH, nav: 3 };
      else if (segments[0] === "tortuga")
        return { lang: LANGUAGE_TYPES.ENGLISH, nav: 4 };

      return { lang: LANGUAGE_TYPES.ENGLISH, nav: 0 };
    } else return { lang: LANGUAGE_TYPES.ENGLISH, nav: 0 };
  };

  const onClickMenu = (menu: number) => {
    setNavIndex(menu);
    navigateTo(language, PATHS[menu]);
  };

  const onSelect = (code: string) => {
    setSelect(code);
    if (code === "GB") {
      setLanguage(LANGUAGE_TYPES.ENGLISH);
      const { lang, nav } = analyseUrl();
      navigateTo(LANGUAGE_TYPES.ENGLISH, PATHS[nav]);
    } else if (code === "CN") {
      setLanguage(LANGUAGE_TYPES.CHINESE);
      const { lang, nav } = analyseUrl();
      navigateTo(LANGUAGE_TYPES.CHINESE, PATHS[nav]);
    }
  };

  return (
    <MenuContainer status={displayMenu}>
      <MmenuItem onClick={() => onClickMenu(0)} isActive={navIndex === 0}>
        {t("Home")}
      </MmenuItem>
      <MmenuItem onClick={() => onClickMenu(1)} isActive={navIndex === 1}>
        {t("Sale")}
      </MmenuItem>
      <MmenuItem onClick={() => onClickMenu(2)} isActive={navIndex === 2}>
        {t("Bermuda")}
      </MmenuItem>
      <MmenuItem onClick={() => onClickMenu(3)} isActive={navIndex === 3}>
        {t("OTC")}
      </MmenuItem>
      <MmenuItem onClick={() => onClickMenu(4)} isActive={navIndex === 4}>
        {t("Tortuga")}
      </MmenuItem>
      {connected && (
        <Header
          connected={connected}
          address={address}
          chainId={chainId}
          killSession={killSession}
        />
      )}
      {!displayMenu && (
        <div style={{ display: "flex", alignItems: "center" }}>
          {connected && chainId === 42161 && (
            <ArbitrumIcon
              src={arbitrum}
              onClick={(e) =>
                window.open("https://twitter.com/buccaneerV3", "_blank")
              }
            />
          )}
          {connected &&
            chainId !== 42161 &&
            (language === LANGUAGE_TYPES.ENGLISH ? (
              <TextMenuIcon>
                <label>Wrong</label>
                <label>Network</label>
              </TextMenuIcon>
            ) : (
              <TextMenuIcon>错误网络</TextMenuIcon>
            ))}
          {/* {connected && (
            <label
              style={{
                color: chainId === 42161 ? "green" : "red",
                fontSize: "50px",
              }}
            >
              •
            </label>
          )} */}
          <ReactFlagsSelect
            selected={select}
            onSelect={onSelect}
            countries={["GB", "CN"]}
            customLabels={{
              GB: { primary: "English" },
              CN: { primary: "中文" },
            }}
            className="language-selector"
            selectButtonClassName="language-flags-button"
          />
          <SocialIcon
            src={twitter}
            onClick={(e) =>
              window.open("https://twitter.com/buccaneerV3", "_blank")
            }
          />
          <SocialIcon
            src={telegram}
            onClick={(e) => window.open("https://t.me/BuccaneerV3", "_blank")}
          />
          <SocialIcon
            src={discord}
            onClick={(e) =>
              window.open("https://discord.com/invite/YhDDGp5wWV", "_blank")
            }
          />
          <SocialIcon src={wallet} onClick={openModal} />
        </div>
      )}
    </MenuContainer>
  );
};

export default MenuArea;
